import React, { useEffect, useMemo, useState } from "react";
import { useField } from "formik";
import { keyBy } from "@common/helpers/collections";
import { TransferRuleDirection } from "@common/types/transactionRule";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { FinancialAccountCombobox } from "@components/financialAccount/AccountsFilter/FinancialAccountCombobox";
import { TransactionRuleConditionFormSchema } from "../constants";
import { useEntitiesFromFormCondition } from "../useEntitiesFromFormCondition";
import { getFieldNamePrefix } from "../../utils";

interface Props {
    index: number;
    transferDirection?: TransferRuleDirection;
    controlButtonSize?: TriggerButtonProps["size"];
}

export const AccountConditionForm: React.FC<Props> = ({
    index,
    transferDirection,
    controlButtonSize,
}) => {
    const [excludedAccountIds, setExcludedAccountIds] = useState<number[]>([]);
    const accountFieldName = `${getFieldNamePrefix(index, transferDirection)}.accountValue`;
    const [accountField, , accountHelpers] =
        useField<TransactionRuleConditionFormSchema["accountValue"]>(
            accountFieldName,
        );
    const subaccountFieldName = `${getFieldNamePrefix(index, transferDirection)}.subaccountValue`;
    const [subaccountField, , subaccountHelpers] =
        useField<TransactionRuleConditionFormSchema["subaccountValue"]>(
            subaccountFieldName,
        );
    const displayedEntities = useEntitiesFromFormCondition({
        transferDirection,
    });

    const accounts = useFinancialAccounts();
    const accountsById = useMemo(
        () => keyBy(accounts, (a) => a.id),
        [accounts],
    );

    useEffect(() => {
        if (displayedEntities.length === 0) {
            return;
        }

        setExcludedAccountIds(
            accounts
                .map((fa) =>
                    displayedEntities.every((e) => e.id !== fa.entityId)
                        ? fa.id
                        : undefined,
                )
                .filter((id) => id !== undefined),
        );
    }, [displayedEntities, accounts]);

    useEffect(() => {
        // Update accounts selection when entity filter changes
        if (!Array.isArray(accountField.value)) {
            return;
        }
        const displayedEntitiesIds = displayedEntities.map((e) => e.id);
        const newValue = accountField.value.filter(
            (v) =>
                accountsById[v]?.entityId &&
                displayedEntitiesIds.includes(accountsById[v].entityId),
        );

        if (newValue.length !== accountField.value.length) {
            accountHelpers.setValue(newValue);
        }
    }, [displayedEntities, accountField.value, accountsById, accountHelpers]);

    return (
        <FinancialAccountCombobox
            multiple
            value={accountField.value ?? []}
            subaccountIds={subaccountField.value ?? []}
            onValueChange={(v) => accountHelpers.setValue(v)}
            onSubaccountChange={(v) => subaccountHelpers.setValue(v)}
            buttonSize={controlButtonSize ?? "md"}
            excludedAccountIds={excludedAccountIds}
        />
    );
};
